import Component from "../../../../lib/component";
import ComponentEvent from "../../../../lib/component_event";

export const InquiryMultiFieldConfig = {
    baseHtmlClass: 'inquiry_field',
    selector: '.inquiry_field',
    inputSelector: 'select,input,textarea',
    labelSelector: 'label',
    valueElementBaseHtmlClass: 'inquiry_field__value_wrap',
    valueElementSelector: '.inquiry_field__value_wrap',

    changeEventName: 'InquiryFieldChanged'
};

// The InquiryMultiField component supports fields with one or more inputs (form elements eg `input` or `select`).
export class InquiryMultiField extends Component {

    static createHiddenInputElement(name) {
        let element = document.createElement('input');
        element.setAttribute("name", name);
        element.setAttribute("type", 'hidden');
        return element;
    }

    static createValueElement() {
        return InquiryMultiField.createElement(InquiryMultiFieldConfig.valueElementBaseHtmlClass);
    }

    constructor(id_or_element, inquiryForm) {
        super({
            element: id_or_element,
            config:  InquiryMultiFieldConfig
        });
        this.inquiryForm = inquiryForm;

        this.setInputElements();
        this.setValueFromInputs();

        this.required = this.getRequiredState();
        this.errorChecking = true;
        this.errors = [];
        this.warningChecking = true;
        this.warnings = [];

        this.labelElement = this.element.querySelector(InquiryMultiFieldConfig.labelSelector);
        this.label = (this.labelElement ? this.labelElement.getAttribute('field_label') : '');

        // Create valueElement to communicate the current field value.
        this.valueElement = InquiryMultiField.createValueElement();
        this.element.insertBefore(this.valueElement,null);
        this.valueTemplate = (value) => { return `<p>${value}</p>` };

        this.placeholders =  {};
        this.inputElements.forEach( (input) => {
            const inputName = input.getAttribute('name');
            const inputPlaceholder = input.getAttribute('placeholder') || '';
            if (inputName) { this.placeholders[inputName] = inputPlaceholder }
        });

        // We set up a listener reference because descendents of this class may wish to make alts.
        this.changeListenerReference = this.handleInputChangeEvent.bind(this);
        this.inputElements.forEach( input => input.addEventListener('change', this.changeListenerReference) );

        this.inputElements.forEach( (input) => {
            input.addEventListener('focus', (event) => {
                this.addHtmlClassState('focused');
                event.target.removeAttribute('placeholder');
            })
        } );

        this.inputElements.forEach( (input) => {
            input.addEventListener('blur', (event) => {
                this.removeHtmlClassState('focused');
                event.target.setAttribute('placeholder', this.placeholders[event.target.getAttribute('name')]);
            });
        });

        this.setInputsRequiredState(false); // Disables built-in browser validation;

        this.update();
        this.render();
    }

    getRequiredState() {
        let required = false;
        if (this.element.dataset['inquiry_field_is_required'] === 'true') { required = true }
        return required;
    }

    isRequired() {
        return this.required;
    }

    setInputElements() {
        const nodeList = this.element.querySelectorAll(InquiryMultiFieldConfig.inputSelector);
        this.inputElements = Array.prototype.slice.call(nodeList); // Creates an array rather than a NodeList.
    }

    setValueFromInputs() {
        this.value = null;
        const inputsValue = this.getInputsValue();
        if (inputsValue !== '') { this.value = inputsValue }
    }

    // Returns an array of typecast values by looking at each of the inputs.
    // Descendents of this class may override as appropriate.
    getInputsValues() {
        let values = [];
        this.inputElements.forEach((el)=> {
            const v = this.typecastValue(el.value);
            if (v && v !== '') { values.push(v) }
        });
        return values;
    }

    // Returns the single typecast value as calculated by looking at each of the inputs.
    // Could be a string, int, date or even an array of similar.
    // Descendents of this class may override as appropriate.
    getInputsValue() {
        const values = this.getInputsValues();
        if (values.length < 1) { return null }
        const sum = (accumulator, currentValue) => accumulator + currentValue;
        return (values.reduce(sum));
    }

    // Sets the value for each of the inputs, from the passed in array of values.
    // Descendents of this class may override as appropriate.
    setInputsValues(values) {
        for (let i=0; i < values.length; i++) {
            this.inputElements[i].value = values[i];
        }
    }

    // Descendents of this class may override with appropriate typecasting.
    typecastValue(value) {
        return value;
    }

    typecastValueToString(value) {
        if (value === '' || value === null) { return null }
        value = String(value);
        return value;
    }

    typecastValueToNumber(value) {
        if (value === '' || value === null) { return null }
        value = Number(value);
        return value;
    }

    typecastValueToBoolean(value) {
        if (value === '' || value === null) { return null }
        if (value === '0') { value = false }
        if (value === '1') { value = true }
        return value;
    }

    // Descendents of this class may override with custom logic.
    // eg. `this.value !== null && this.value !== 0`
    hasValue() {
        return this.value !== null && (Array.isArray(this.value) && this.value.length > 0);
    }

    // Returns the value in an appropriate human-readable form.
    // Descendents of this class may override with custom logic.
    humanValue() {
        return this.value;
    }


    // Descendents of this class may override to perform tasks before the values are checked.
    beforeUpdate() {
    }

    // Descendents of this class may override to perform tasks after the values are checked.
    afterUpdate() {
    }

    update() {
        this.beforeUpdate();

        this.setValueFromInputs();
        this.setErrors();
        this.setWarnings();

        this.afterUpdate();
    }


    // Descendents of this class may override and push errors onto the the errors array.
    // eg. `if ( condition_not_met ) { this.errors.push(`The ${ this.value } value on ${ this.label } is invalid`) };
    appendErrors() {
    }

    // Descendents of this class may override and push warnings onto the the warnings array.
    // eg. `if ( condition_not_met ) { this.warnings.push(`The ${ this.value } value on ${ this.label } may be a problem`) };
    appendWarnings() {
    }

    setErrors() {
        this.errors = [];
        if (this.errorChecking) { this.appendErrors() }
    }

    setWarnings() {
        this.warnings = [];
        if (this.warningChecking) { this.appendWarnings() }
    }

    hasErrors() {
        return this.errors && this.errors.length > 0;
    }

    hasWarnings() {
        return this.warnings && this.warnings.length > 0;
    }

    setInputsRequiredState(state) {
        this.inputElements.forEach( (input) => { input.required = !!state });
    }

    renderValueToDataAttribute() {
        const valueAsString = this.hasValue() ? String(this.value) : '';
        this.element.setAttribute('data-inquiry_field_value', valueAsString);
    }

    renderHumanValueToValueElement() {
        let content = this.humanValue();
        if (this.valueTemplate) { content = this.humanValue() ? this.valueTemplate(this.humanValue()) : '' }
        this.valueElement.innerHTML = content;
    }

    render() {
        this.renderValueToDataAttribute();
        this.renderHumanValueToValueElement();

        this.conditionallySetHtmlClassState(this.hasValue(), 'has_value');
        this.conditionallySetHtmlClassState(this.hasErrors(), 'has_errors');
        this.conditionallySetHtmlClassState(this.hasWarnings(), 'has_warnings');
    }

    handleInputChangeEvent() {
        this.update();
        this.fireChangeEvent();
        this.inquiryForm.fireChangeEvent();
        this.render();
    }

    fireChangeEvent() {
        const eventName = InquiryMultiFieldConfig.changeEventName;
        const componentEvent = new ComponentEvent(eventName, { target: this })
        this.fireEvent(componentEvent);
    }

}